import React, {
  useContext,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
} from 'react';

import { loginSessionStorage } from 'storage/auth';
import { validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';

const TOKEN_LENGTH = 6;

type MfaChallengeMethod = 'email' | 'totp' | null;

type NullString = string | null;

type Ctx = {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  errorMessage: NullString;
  setErrorMessage: Dispatch<SetStateAction<NullString>>;
  emailErrorMessage: NullString;
  setEmailErrorMessage: Dispatch<SetStateAction<NullString>>;
  submitEmail: (email: string) => void;
  mfaOption: MfaChallengeMethod;
  setMfaOption: Dispatch<SetStateAction<MfaChallengeMethod>>;
  mfaToken: NullString;
  setMfaToken: (token: NullString) => void;
  mfaRemember: boolean;
  setMfaRemember: Dispatch<SetStateAction<boolean>>;
};

export const validToken = (token: NullString) => token?.length === TOKEN_LENGTH;

const LoginContext = React.createContext<Ctx>({
  email: '',
  setEmail: () => null,
  password: '',
  setPassword: () => null,
  errorMessage: '',
  setErrorMessage: () => null,
  emailErrorMessage: '',
  setEmailErrorMessage: () => null,
  submitEmail: (email) => null,
  mfaOption: null,
  setMfaOption: () => null,
  mfaToken: '',
  setMfaToken: (token) => null,
  mfaRemember: false,
  setMfaRemember: () => null,
});

export const LoginProvider = ({ children }: PropsWithChildren) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, _setMfaToken] = useState<NullString>(null);
  const [errorMessage, setErrorMessage] = useState<NullString>('');
  const [emailErrorMessage, setEmailErrorMessage] = useState<NullString>('');
  const [mfaOption, setMfaOption] = useState<MfaChallengeMethod>(null);
  const [mfaRemember, setMfaRemember] = useState<boolean>(false);

  const submitEmail = useCallback(
    (email: string) => {
      if (validateEmail(email)) {
        loginSessionStorage.val = email;
        setEmail(email);
        setErrorMessage(null);
      } else {
        setErrorMessage('Please enter a valid email address');
      }
    },
    [setEmail]
  );

  const setMfaToken = useCallback(
    (token: NullString) => {
      if (token === null || token.length <= TOKEN_LENGTH) {
        _setMfaToken(token);
      }
    },
    [_setMfaToken]
  );

  return (
    <LoginContext.Provider
      value={{
        email,
        setEmail,
        password,
        setPassword,
        errorMessage,
        setErrorMessage,
        submitEmail,
        emailErrorMessage,
        setEmailErrorMessage,
        mfaOption,
        setMfaOption,
        mfaToken,
        setMfaToken,
        mfaRemember,
        setMfaRemember,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginState = () => {
  const ctx = useContext(LoginContext);
  return ctx;
};
